import { GetterTree, MutationTree, ActionTree, Module } from 'vuex';
import { LoginState, RootState } from '@/types';
import LoginService from '@/services/login.service';
import LogRocket from 'logrocket';
import { ApiManager, IntegrationsServiceAPI, DataServiceAPI, ApiService, ApiV2Service } from '@/services/api.base.service';
import Vue from 'vue';
import Transformation from '../../classes/transformation';
import store from '@/store/store';

type LoginGetter = GetterTree<LoginState, RootState>;

export const initialState = (): LoginState => {
  return {
    initialState,
    token: null,
    details: {first_name: '', last_name: '', email: '', mfa_enabled: false},
    permissions: [],
    originalToken: null,
    lastOrganisation: null,
    experimental: false,
    showAllOrganisations: true,
  };
};

export const getters: LoginGetter = {
  login: (state) => state,
  userDetails: (state) => state.details,
  permissions: (state) => state.permissions,
  hasDaizyDebug: (state) => state.permissions.includes('daizy:debug'),
  hasPermission: (state) => (permission: string) => {
    if (state.permissions.includes(permission)) {
      if (permission !== 'daizy:debug' || state.experimental) {
        return true;
      }
    }
    return false;
  },
  isImpersonated: (state) => state.originalToken != null,
  lastOrganisation: (state) => state.lastOrganisation,
  experimental: (state) => state.experimental,
  showAllOrganisations: (state) => state.showAllOrganisations,
};

export const mutations: MutationTree<LoginState> = {
  addToken(state, newLogin) {
    state.token = newLogin.token;
    ApiManager.setToken(newLogin.token);
  },
  impersonate(state, newLogin) {
    state.originalToken = state.token;
    state.token = newLogin.token;
    ApiManager.setToken(newLogin.token);
  },
  stopImpersonate(state) {
    if (state.originalToken) {
      state.token = state.originalToken;
      ApiManager.setToken(state.originalToken);
      state.originalToken = null;
    }
  },
  removeToken(state) {
    state.token = null;
    ApiManager.removeToken();
  },
  addUserDetails(state, details) {
    state.details = details;
  },
  updatePermissions(state, newPermissions) {
    state.permissions = newPermissions;
  },
  setLastOrganisation(state, org) {
    state.lastOrganisation = org;
  },
  setExperimental(state, value: boolean) {
    state.experimental = value;
  },
  toggleShowAllOrganisations(state, value: boolean) {
    state.showAllOrganisations = value;
  },
};

export const actions: ActionTree<LoginState, RootState> = {
  async addTokenAsync(context, credentials) {
    const token = await LoginService.login(credentials);
    if (token) {
      context.commit('addToken', token);
      await context.dispatch('userDetailsAsync');
      await context.dispatch('addOrganisationsAsync');
      await context.dispatch('addAccountsAsync');
    }
    return token;
  },

  async userLogout(context) {
    context.commit('setShowInterface', false);
    context.commit('stopImpersonate');
    context.commit('removeToken');
    context.commit('updatePermissions', []);
    context.commit('setLastOrganisation', store.getters.lastOrganisation);

    location.replace(`${process.env.VUE_APP_COGNITO_HOST}/logout?client_id=${process.env.VUE_APP_COGNITO_CLIENT_ID}&logout_uri=${location.protocol}//${window.location.host}/login`);
  },

  async userDetailsAsync(context) {
    const user = await LoginService.me();
    context.commit('addUserDetails', user);
    if (process.env.VUE_APP_LOGROCKET_ID) {
      if (user.email !== 'devadmin@daizy.io') {  // automated canary account
        LogRocket.init(process.env.VUE_APP_LOGROCKET_ID);
        LogRocket.identify(user.id.toString(), {
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
      });
      }
    }
    // If running in development and we have a secret create a new token
    if (Vue.config.devtools && process.env.VUE_APP_JWT_SECRET) {
      const newToken = Transformation.createAuthorizerJWT(context.getters.userDetails, context.getters.permissions, process.env.VUE_APP_JWT_SECRET);
      context.commit('addToken', {token: newToken});
    }
    return user;
  },

  async updateMeAsync(context: any, device: { id: number, payload: object }) {
    const user = await LoginService.updateMe(device.payload);

    context.commit('addUserDetails', user);
    return user;
  },

  async updatePermissionsAsync(context: any, orgId: number) {
    const permissions = await LoginService.getPermissions(orgId, true);

    context.commit('updatePermissions', permissions);
    // If running in development and we have a secret create a new token
    if (Vue.config.devtools && process.env.VUE_APP_JWT_SECRET) {
      const newToken = Transformation.createAuthorizerJWT(context.getters.userDetails, permissions, process.env.VUE_APP_JWT_SECRET);
      context.commit('addToken', {token: newToken});
    }

    return Promise.resolve({});
  },
};

export const login: Module<LoginState, RootState> = {
  state: initialState(),
  getters,
  mutations,
  actions,
};
